import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { DefaultTable } from "components";
import { ReducedUserProfileWithEmail, UserProfile } from "api/user";
import { deleteAliasAccounts, getAliasAccounts } from "api/alias";
import CreateAliasAccountDialog from "./CreateAliasAccountDialog";
import i18n from "./i18n";
import EditContactDataDialog from "./EditContactDataDialog";

// prop types
interface AliasAccountsTabProps {
    clubId?: string,
    fromAdminMenu?: boolean,
    userProfile: UserProfile | null,
}

// alias accounts table
const AliasAccountsTab = ({ clubId, fromAdminMenu, userProfile }: AliasAccountsTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [aliasAccounts, setAliasAccounts] = useState<ReducedUserProfileWithEmail[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [showCreateAliasDialog, setShowCreateAliasDialog] = useState(false);
    const [showEditContactDataDialog, setShowEditContactDataDialog] = useState<string | null>(null);

    // fetch accounts
    const fetchAliasAccounts = async () => {
        setLoading(true);
        try {
            setAliasAccounts(await getAliasAccounts({ clubId: clubId! }));
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };
    useEffect(() => { if (clubId) fetchAliasAccounts(); }, [clubId]); // eslint-disable-line

    // delete row handler (delete account)
    const onRowDelete = (oldData: ReducedUserProfileWithEmail) => new Promise<void>((resolve) => {
        deleteAliasAccounts({
            aliasUserIds: [oldData.id],
        }).then(async () => {
            await fetchAliasAccounts();
            resolve();
        }).catch((err) => { displayError(err); resolve(); });
    });

    // table actions
    const tableActions = [
        {
            icon: "add",
            isFreeAction: true,
            tooltip: i18n.createAliasAccount,
            onClick: () => setShowCreateAliasDialog(true),
        },
        ({ id }: ReducedUserProfileWithEmail) => ({
            icon: "edit",
            tooltip: i18n.editContactData,
            onClick: () => setShowEditContactDataDialog(id),
        }),
    ];

    // table columns
    const tableColumns = [
        {
            field: "firstName",
            title: i18n.firstName,
        },
        {
            field: "lastName",
            title: i18n.lastName,
        },
        {
            field: "email",
            title: i18n.emailAddress,
        },
    ];

    // render
    return (
        <>
            {/* alias accounts table */}
            <DefaultTable
                actions={tableActions}
                columns={tableColumns}
                data={aliasAccounts}
                editable={{ onRowDelete }}
                isLoading={isLoading}
                options={{ paging: false }}
                title={i18n.aliasAccounts}
            />

            {/* create alias account dialog */}
            <CreateAliasAccountDialog
                clubId={clubId}
                fromAdminMenu={fromAdminMenu}
                show={showCreateAliasDialog}
                userProfile={userProfile}
                onClose={() => setShowCreateAliasDialog(false)}
                onFetchAliasAccounts={fetchAliasAccounts}
            />

            {/* edit contact data dialog */}
            <EditContactDataDialog
                aliasUserId={showEditContactDataDialog || undefined}
                show={!!showEditContactDataDialog}
                onClose={() => setShowEditContactDataDialog(null)}
            />
        </>
    );
};

export default AliasAccountsTab;
